// apps/web/src/widgets/summary-checklist/renderer.tsx
//
// Phase 6 — WIDG-05 SummaryChecklistRenderer (D-03..D-07). // sanitize-allow: WIDG-05
//
// Implements:
//   - per-item visibility via RecipeStateContext + isVisibleById (D-04 + D-16)
//     (06-REVIEW WR-01: each item resolves its OWN source widget's state via
//      the referenced visibility constraint's `when.ref.widgetInstanceId` —
//      NOT config.source.sourceWidgetId)
//   - section grouping by groupId when config.groupBy === "category" (D-06)
//   - "{checked}/{total} DONE" per-section counter (accent when all checked)
//   - view-mode toggle: Title / Snippet / Full / Expanded (D-03)
//   - combined two-stage Reset-views + Clear-checkmarks confirm (4s arming
//     window; ONE atomic onStateChange write) — Phase 11.7 Plan 04 Task 1
//   - ephemeral per-section collapse (aria-expanded header buttons) — 11.7-04
//
// Phase 9 Plan 09-05 D-16 — inline `visibleWhen` on items REPLACED by
// `visibilityConstraintId` referencing a recipe-level visibility Constraint
// (recipe.constraints[]). The renderer reads `isVisibleById(constraintId,
// recipe, ctx.states)` per item; the legacy `isVisible(predicate, state)`
// export is preserved on the shim for OptionCategory (Phase 8 AUTHR-02)
// callers but no longer used in this file.
// Section collapse landed in Phase 11.7 (this file). Sticky positioning and
// spread-level layout remain out of scope here — per the UI-SPEC parity notes
// (informational) those belong to the spread shell.
import { Check, ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import { useContext, useEffect, useRef, useState } from "react";
import { RecipeStateContext } from "../recipe-state-provider.js";
import type { WidgetRendererProps } from "../widget-renderer-props.js";
import { resolveGroupLabel } from "./group-label.js";
import { isVisibleById } from "./visible-when.js";

type ViewMode = "title" | "snippet" | "full" | "expanded";

const VIEW_MODES: ReadonlyArray<{ id: ViewMode; label: string }> = [
  { id: "title", label: "Title" },
  { id: "snippet", label: "Snippet" },
  { id: "full", label: "Full" },
  { id: "expanded", label: "Expanded" },
];

const ARMED_TIMEOUT_MS = 4000;

function snippet(description: string): string {
  return description.length > 80 ? `${description.slice(0, 80)}…` : description;
}

// 06-UI-REVIEW C-1: section header resolves groupId to recipe-declared
// OptionCategory.label — `resolveGroupLabel` (+ its `GroupLabelSource` type)
// is LIFTED into ./group-label.ts (Plan 11.7-04 Task 0, the Surface 1/3
// shared seam). The call site below is unchanged; only the import source moved.
export function SummaryChecklistRenderer({
  widget,
  config,
  recipe,
  state,
  onStateChange,
}: WidgetRendererProps<"summary-checklist">): React.ReactElement {
  const checkedItems: ReadonlyArray<string> = state?.checkedItems ?? [];
  const viewMode: ViewMode = state?.viewMode ?? config.viewMode ?? "title";

  // 06-REVIEW WR-01: per-item visibleWhen lookup
  // ----------------------------------------------------------------------
  // Each item's `visibleWhen.sourceWidgetId` is what drives that item's
  // visibility — it MAY differ from `config.source.sourceWidgetId` (which
  // is the checklist's data-source binding for Phase 11 wiring).
  // Reading the provider Map directly (rather than calling
  // `useOtherWidgetState` per item, which would violate Rules of Hooks)
  // lets each item resolve its own source widget's state.
  const ctx = useContext(RecipeStateContext);
  if (ctx === null) {
    throw new Error("<SummaryChecklistRenderer> must be rendered inside <RecipeStateProvider>");
  }

  const [armed, setArmed] = useState<"idle" | "armed">("idle");
  const armedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 11.7-04 Task 1 — ephemeral per-section collapse. NO persisted state, NO
  // WidgetState lockstep, NO schema touch (CONTEXT-locked): collapse never
  // round-trips to 11.3 persistence. The map is keyed by the finite set of
  // section group keys; default (empty map) = all expanded.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    return () => {
      if (armedTimerRef.current !== null) {
        clearTimeout(armedTimerRef.current);
      }
    };
  }, []);

  const visibleItems = config.items.filter((item) =>
    // Per-item visibility — `isVisibleById` resolves the constraint via
    // document-position id and evaluates `when.ref.widgetInstanceId` against
    // ctx.states. Defaults to true for unresolved / non-visibility kinds
    // (Plan 09-PATTERNS Pitfall 7).
    isVisibleById(item.visibilityConstraintId, recipe, ctx.states),
  );

  const setViewMode = (next: ViewMode): void => {
    onStateChange({ checkedItems, viewMode: next });
  };

  // 11.7-04 Task 1 — combined two-stage Reset+Clear. Re-wires the existing
  // arm/confirm machinery so ONE control does BOTH effects. The confirm branch
  // performs a SINGLE atomic write {viewMode: default, checkedItems: []} —
  // NOT two sequential writes, which would double-fire the debounced 11.3 save
  // (RESEARCH Open Q2). `window.confirm()` is deliberately rejected in favor of
  // the in-surface armed treatment.
  const onResetClearClick = (): void => {
    if (armed === "armed") {
      if (armedTimerRef.current !== null) {
        clearTimeout(armedTimerRef.current);
        armedTimerRef.current = null;
      }
      onStateChange({ viewMode: config.viewMode ?? "title", checkedItems: [] });
      setArmed("idle");
      return;
    }
    setArmed("armed");
    armedTimerRef.current = setTimeout(() => {
      setArmed("idle");
      armedTimerRef.current = null;
    }, ARMED_TIMEOUT_MS);
  };

  const toggleItem = (itemId: string): void => {
    const next = checkedItems.includes(itemId)
      ? checkedItems.filter((x) => x !== itemId)
      : [...checkedItems, itemId];
    onStateChange({ viewMode, checkedItems: next });
  };

  // Group items per config.groupBy. Order of groups is the order their first
  // member appears in visibleItems; group "none" is the unbucketed fallback.
  const groupOrder: string[] = [];
  const groups = new Map<string, typeof visibleItems>();
  for (const item of visibleItems) {
    const key = config.groupBy === "category" ? (item.groupId ?? "_ungrouped") : "_flat";
    if (!groups.has(key)) {
      groups.set(key, []);
      groupOrder.push(key);
    }
    groups.get(key)?.push(item);
  }

  // 11.7-04 UI-SPEC Copywriting Contract (these strings live in widgets/** so
  // the pages/spread/** vocab gate does NOT scan them — kept generic regardless).
  const resetClearIdleLabel = "Reset & clear";
  const resetClearArmedLabel = "Confirm reset & clear";
  const resetClearAriaLabel = "Reset views and clear all checkmarks";

  // 06-UI-REVIEW V-2: fcb-head aggregate "X/Y DONE" summary slot.
  // Aggregates across CURRENTLY VISIBLE items only — `visibleWhen`-hidden
  // items are excluded from both numerator and denominator so the counter
  // doesn't show 0/0 when all conditional items are hidden, and doesn't
  // count a checked-then-hidden item against the total.
  const aggregateTotal = visibleItems.length;
  const aggregateChecked = visibleItems.filter((it) => checkedItems.includes(it.id)).length;
  const aggregateAllChecked = aggregateTotal > 0 && aggregateChecked === aggregateTotal;

  return (
    <section
      aria-label={`${widget.name} checklist`}
      className="flex flex-col gap-3 rounded-lg border border-[var(--recipe-line)] bg-[var(--recipe-card)] p-4"
    >
      {/* fcb-head bar — title + aggregate counter + view-mode toggle + reset + clear */}
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-base font-semibold leading-tight text-[var(--recipe-ink)]">
          {widget.name}
        </h3>
        {/* V-2: aggregate "X/Y DONE" across visible items */}
        <span
          aria-live="polite"
          className={`text-xs font-bold ${
            aggregateAllChecked ? "text-[var(--recipe-accent)]" : "text-[var(--recipe-muted)]"
          }`}
        >
          {aggregateChecked}/{aggregateTotal} DONE
        </span>
        {/* View-mode radio group */}
        <div role="radiogroup" aria-label="Display mode" className="flex gap-1">
          {VIEW_MODES.map((m) => (
            // biome-ignore lint/a11y/useSemanticElements: view-mode toggle is part of the recipe widget surface; the design system requires custom-styled buttons inside a radiogroup, not native <input type="radio">. UI-SPEC §"summary-checklist renderer".
            <button
              key={m.id}
              type="button"
              role="radio"
              aria-checked={viewMode === m.id}
              onClick={() => setViewMode(m.id)}
              className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                viewMode === m.id
                  ? "bg-[var(--recipe-accent)] text-white"
                  : "bg-transparent text-[var(--recipe-ink)] hover:bg-[var(--recipe-accent-soft)]"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
        {/* Combined two-stage Reset-views + Clear-checkmarks confirm */}
        <button
          type="button"
          aria-label={resetClearAriaLabel}
          onClick={onResetClearClick}
          className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
            armed === "armed"
              ? "bg-destructive text-destructive-foreground"
              : "bg-transparent text-[var(--recipe-muted)] hover:text-[var(--recipe-ink)]"
          }`}
        >
          <RotateCcw size={14} aria-hidden />
          <span>{armed === "armed" ? resetClearArmedLabel : resetClearIdleLabel}</span>
        </button>
        {armed === "armed" ? (
          <span className="sr-only" aria-live="polite">
            {resetClearArmedLabel}
          </span>
        ) : null}
      </div>
      {/* Item sections */}
      {groupOrder.map((key) => {
        const items = groups.get(key) ?? [];
        const checkedCount = items.filter((it) => checkedItems.includes(it.id)).length;
        const total = items.length;
        const allChecked = total > 0 && checkedCount === total;
        const showGroupHeader = config.groupBy === "category";
        // 06-UI-REVIEW T-1: section header uses Label typography
        // (text-xs uppercase tracking-wider font-semibold leading-5) per
        // UI-SPEC §Typography. Default color is muted; complete state escalates
        // to recipe accent (Q6 recommendation).
        const headerColor = allChecked
          ? "text-[var(--recipe-accent)]"
          : "text-[var(--recipe-muted)]";
        // 06-UI-REVIEW C-1: resolve groupId to recipe-declared category label.
        const groupLabel = resolveGroupLabel(key, recipe.optionCategories);
        // 11.7-04 Task 1 — ephemeral collapse. Only grouped (category) sections
        // are collapsible; the flat fallback has no header button. The header
        // button is a real focusable control (Space/Enter native) — an a11y
        // upgrade over the reference's bare clickable <h4>.
        const isCollapsed = showGroupHeader ? collapsed[key] === true : false;
        const itemsId = `${widget.instanceId}-section-${key}`;
        return (
          <section
            key={key}
            aria-label={showGroupHeader ? groupLabel || key : `checklist section ${key}`}
            className="flex flex-col gap-1"
          >
            {showGroupHeader ? (
              <h4 className="contents">
                <button
                  type="button"
                  aria-expanded={!isCollapsed}
                  aria-controls={itemsId}
                  onClick={() => setCollapsed((c) => ({ ...c, [key]: !c[key] }))}
                  className={`flex w-full items-center gap-1 text-xs font-semibold uppercase tracking-wider leading-5 ${headerColor}`}
                >
                  {isCollapsed ? (
                    <ChevronRight size={14} aria-hidden />
                  ) : (
                    <ChevronDown size={14} aria-hidden />
                  )}
                  {allChecked ? <Check size={12} aria-hidden /> : null}
                  <span>{groupLabel}</span>
                  <span
                    className={`ml-auto text-xs font-bold ${allChecked ? headerColor : "text-[var(--recipe-muted)]"}`}
                  >
                    {checkedCount}/{total} DONE
                  </span>
                </button>
              </h4>
            ) : (
              <div className={`text-xs ${allChecked ? headerColor : "text-[var(--recipe-muted)]"}`}>
                {checkedCount}/{total} DONE
              </div>
            )}
            {/* aria-controls target stays in the DOM (valid reference) but its
                items are unrendered when collapsed — the header row + DONE
                count above remain visible. */}
            <ul id={itemsId} hidden={isCollapsed} className="flex flex-col gap-1">
              {isCollapsed
                ? null
                : items.map((item) => {
                    const isChecked = checkedItems.includes(item.id);
                    const inputId = `${widget.instanceId}-${item.id}`;
                    return (
                      <li key={item.id} className="flex flex-col gap-1">
                        <label
                          htmlFor={inputId}
                          className="inline-flex items-start gap-2 text-sm text-[var(--recipe-ink)]"
                        >
                          <input
                            id={inputId}
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleItem(item.id)}
                            className="mt-1 accent-[var(--recipe-accent)]"
                          />
                          <span className="flex flex-col gap-1">
                            <span>
                              {item.title}
                              {viewMode === "snippet" && item.description !== undefined ? (
                                <span className="text-[var(--recipe-muted)]">
                                  {" — "}
                                  {snippet(item.description)}
                                </span>
                              ) : null}
                            </span>
                            {(viewMode === "full" || viewMode === "expanded") &&
                            item.description !== undefined ? (
                              <span className="text-xs text-[var(--recipe-muted)]">
                                {item.description}
                              </span>
                            ) : null}
                            {viewMode === "expanded" &&
                            item.subItems !== undefined &&
                            item.subItems.length > 0 ? (
                              <ul className="ml-4 mt-1 list-disc text-xs text-[var(--recipe-muted)]">
                                {item.subItems.map((sub) => (
                                  <li key={sub.id}>{sub.title}</li>
                                ))}
                              </ul>
                            ) : null}
                          </span>
                        </label>
                      </li>
                    );
                  })}
            </ul>
          </section>
        );
      })}
    </section>
  );
}
